/**
 * JOLeadPopup — Callback-request popup that fires 5 s after the visitor lands
 * on any public page. Asks for prénom, nom, téléphone, and (optionally) which
 * kind of project ("aménagement" or "entretien").
 *
 * Behaviour:
 *   - Appears once per session (localStorage flag)
 *   - Dismissible via ✕ or clicking backdrop
 *   - Never shows on /admin, /agency, or during checkout/account routes
 *   - Submits to POST /api/leads (existing endpoint) with type="rappel"
 *   - Includes anti-spam honeypot + form_started_at (existing anti-bot checks)
 *
 * NON-CORE — additive, mounted globally in App.js.
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "jo_lead_popup_seen";      // localStorage flag
const SUBMITTED_KEY = "jo_lead_popup_done"; // never re-show once submitted
const DELAY_MS = 5000;

const HIDDEN_PREFIXES = [
  "/admin",
  "/agency",
  "/account",
  "/checkout",
  "/cart",
  "/hub",
];

function isPublicRoute(pathname) {
  return !HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function JOLeadPopup() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [projectType, setProjectType] = useState(""); // amenagement | entretien
  const [honeypot, setHoneypot] = useState(""); // anti-bot honeypot field
  const formStartedAt = useMemo(() => Date.now(), []);

  useEffect(() => {
    if (!isPublicRoute(location.pathname)) return;
    // Already submitted or already dismissed this session
    if (sessionStorage.getItem(SEEN_KEY) === "1") return;
    if (localStorage.getItem(SUBMITTED_KEY) === "1") return;

    const timer = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem(SEEN_KEY, "1");
    }, DELAY_MS);
    return () => clearTimeout(timer);
  }, [location.pathname]);

  const close = () => {
    setOpen(false);
  };

  const canSubmit =
    firstName.trim().length >= 2 &&
    lastName.trim().length >= 2 &&
    phone.trim().replace(/\D/g, "").length >= 8 &&
    status !== "sending";

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    setErrorMsg("");
    const service =
      projectType === "amenagement"
        ? "Aménagement extérieur"
        : projectType === "entretien"
        ? "Entretien de jardin"
        : "";
    try {
      const payload = {
        name: `${firstName.trim()} ${lastName.trim()}`,
        phone: phone.trim(),
        email: "",
        message: projectType
          ? `Popup accueil — projet ${service}. Souhaite être rappelé(e).`
          : "Popup accueil — souhaite être rappelé(e).",
        service,
        zone: "",
        type: "rappel",
        source: "popup_home",
        page: location.pathname,
        meta: { source_form: "jo_lead_popup", project_type: projectType || null },
        website: honeypot,          // honeypot — bots fill this
        form_started_at: formStartedAt,
        captcha_answer: "",
      };
      const r = await axios.post(`${API}/leads`, payload, { validateStatus: () => true });
      if (r.status >= 200 && r.status < 300) {
        setStatus("success");
        localStorage.setItem(SUBMITTED_KEY, "1");
        setTimeout(() => setOpen(false), 3500);
      } else {
        setStatus("error");
        setErrorMsg(
          r.data?.detail ||
            "Impossible d'envoyer votre demande pour le moment. Merci de réessayer ou de nous appeler."
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("Réseau indisponible. Merci de réessayer dans quelques instants.");
    }
  };

  if (!open) return null;

  return (
    <div
      className="jo-lead-popup-backdrop"
      data-testid="jo-lead-popup"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div className="jo-lead-popup-card" role="dialog" aria-modal="true" aria-labelledby="jo-lead-popup-title">
        <button
          type="button"
          className="jo-lead-popup-close"
          onClick={close}
          aria-label="Fermer"
          data-testid="jo-lead-popup-close"
        >
          ×
        </button>

        <div className="jo-lead-popup-eyebrow">Un projet ? Une envie ?</div>
        <h3 id="jo-lead-popup-title" className="jo-lead-popup-title">
          Vous avez un <em>projet d&apos;aménagement extérieur</em>&nbsp;?
          <br />
          Ou vous cherchez une <em>solution pour l&apos;entretien</em> de votre jardin&nbsp;?
        </h3>
        <p className="jo-lead-popup-lead">
          Laissez-nous vos coordonnées et un paysagiste des Jardins d&apos;Éole vous rappelle rapidement,
          sans engagement.
        </p>

        {status === "success" ? (
          <div className="jo-lead-popup-success" data-testid="jo-lead-popup-success">
            <div className="jo-lead-popup-success-icon" aria-hidden="true">✓</div>
            <div>
              <strong>Merci {firstName || ""}&nbsp;!</strong>
              <br />
              Nous vous rappelons dès que possible au&nbsp;{phone}.
            </div>
          </div>
        ) : (
          <form className="jo-lead-popup-form" onSubmit={submit} noValidate>
            {/* Honeypot — must stay empty (hidden from real users) */}
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="jo-lead-popup-honeypot"
              aria-hidden="true"
            />

            <div className="jo-lead-popup-project">
              <button
                type="button"
                className={`jo-lead-popup-project-chip ${projectType === "amenagement" ? "is-active" : ""}`}
                onClick={() => setProjectType("amenagement")}
                data-testid="jo-lead-popup-chip-amenagement"
              >
                🏡 Aménagement
              </button>
              <button
                type="button"
                className={`jo-lead-popup-project-chip ${projectType === "entretien" ? "is-active" : ""}`}
                onClick={() => setProjectType("entretien")}
                data-testid="jo-lead-popup-chip-entretien"
              >
                🌿 Entretien
              </button>
            </div>

            <div className="jo-lead-popup-row">
              <label className="jo-lead-popup-field">
                <span>Prénom</span>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Jean"
                  required
                  autoComplete="given-name"
                  data-testid="jo-lead-popup-firstname"
                />
              </label>
              <label className="jo-lead-popup-field">
                <span>Nom</span>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Dupont"
                  required
                  autoComplete="family-name"
                  data-testid="jo-lead-popup-lastname"
                />
              </label>
            </div>
            <label className="jo-lead-popup-field">
              <span>Téléphone</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="06 12 34 56 78"
                required
                autoComplete="tel"
                inputMode="tel"
                data-testid="jo-lead-popup-phone"
              />
            </label>

            {status === "error" && (
              <div className="jo-lead-popup-error" data-testid="jo-lead-popup-error">
                {errorMsg}
              </div>
            )}

            <button
              type="submit"
              className="jo-lead-popup-submit"
              disabled={!canSubmit}
              data-testid="jo-lead-popup-submit"
            >
              {status === "sending" ? "Envoi..." : "Être rappelé(e)"}
            </button>
            <p className="jo-lead-popup-legal">
              Vos données ne seront utilisées que pour vous rappeler à propos de votre demande.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
